package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

/**
 * Request body for `POST /api/pipeline/curated/run` — start a run of the curated
 * workflow registered as [jobType], with the scientist's knob values in [params]
 * and their file picks in [inputs].
 *
 * [jobType] is the registration `id` verbatim (ADV-669 D11: the registration `id`
 * **is** the `job_type`). It is NOT suffixed the way the built-in stages are — see
 * `PipelineSubmitService`.
 *
 * `outdir` is deliberately absent and is rejected if it turns up in [params]:
 * D17 makes it platform-owned, so DataHub supplies it.
 *
 * [outputFolder] is the DataHub folder the run's outputs go under, as a logical path: a
 * folder in the caller's own drive, or `/` for its root, wherever the picks sit. The
 * outputs are then the caller's rows. Null or blank puts them under the anchor input's
 * folder, as the picks' owner's rows. A folder that is not one of the caller's is 404
 * `{"error":"output_folder_not_found"}`. Either way the run
 * writes into its own `<source-prefix>-<runId>` folder there (`PipelineOutputPaths`),
 * which `GET /api/pipeline/curated/runs/{jobId}` names ([CuratedRunStatusResponse]).
 */
@Serializable
data class CuratedRunRequest(
    val jobType: String,
    val params: Map<String, String> = emptyMap(),
    val inputs: List<CuratedInputPicks> = emptyList(),
    val outputFolder: String? = null,
)

/**
 * The scientist's picks for one curated input declaration, keyed by the curator's
 * input [name]. The picks arrive **explicit** — per D21 the picker enumerates a
 * chosen folder through the browse API under the scientist's own session, so
 * DataHub never expands a folder and a FOLDER row is not a valid pick.
 */
@Serializable
data class CuratedInputPicks(
    val name: String,
    val picks: List<CuratedPickDto> = emptyList(),
    /**
     * The curator's declared arity for this input — `"file-path"` or `"file-set"` (D4),
     * or null when the submitter didn't say.
     *
     * DataHub uses it for exactly ONE thing: choosing the run's anchor file
     * (`pipeline_job.file_id`) — see `PipelineRunController.curatedRun` and
     * `CuratedSubmit.anchorInputName`. It is NOT part of the wisp submit body:
     * `CuratedSubmit.render` keys picks by input name and emits only `{file_id, rel}`
     * per pick (D10). Null-tolerant on purpose, so the anchor degrades to the
     * positional rule rather than rejecting the request.
     */
    val format: String? = null,
)

/**
 * The two wire spellings of [CuratedInputPicks.format], shared so both sides agree on
 * one. (The **resolve** wire — a wisp→DataHub call on a different endpoint — keeps its
 * own private copy of these strings in `ResolveInputsWire`; that is a separate code
 * path and is deliberately left alone.)
 */
object CuratedInputFormatWire {
    const val FILE_PATH = "file-path"
    const val FILE_SET = "file-set"
}

/**
 * One picked file. [rel] is the member's path **relative to the pick** — a
 * scattered file's is its basename; a file enumerated out of a picked
 * subdirectory keeps its subtree (`L1/z.fastq.gz`). Blank means "use the file's
 * own name". [rel] is user data; the containment check lives in resolution
 * (`TargetPathRules`), not here.
 */
@Serializable
data class CuratedPickDto(
    val fileId: String,
    val rel: String = "",
)

/**
 * `GET /api/pipeline/curated/runs/{jobId}` — a curated run as it stands. [status] is the
 * job's (`PENDING`, `RUNNING`, `SUCCEEDED`, `FAILED`, `REJECTED`) and [error] why it failed.
 * [runId] is WISP's, null until the job is submitted to it; [outputFolder] is the logical
 * path of the folder the run's outputs went into, from its first output on — null for a
 * run that has written nothing.
 */
@Serializable
data class CuratedRunStatusResponse(
    val jobId: String,
    val jobType: String,
    val status: String,
    val error: String? = null,
    val runId: String? = null,
    val outputFolder: String? = null,
)
